import React, { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '@/shared/lib/logger';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  ClipboardList,
  Keyboard,
  Mic,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useScenarios } from '@/features/alpha/hooks/useScenarios';
import { sendVoice, VoiceStreamEvent as ServiceVoiceStreamEvent } from '@/features/alpha/alpha.service';
import { playBase64AudioChunks } from '@/features/alpha/services/audioPlayback.service';
// eslint-disable-next-line no-restricted-imports
import { Scenario } from '../types/alpha.types';
import KpiCard from './KpiCard';

interface Message {
  text: string;
  isUser: boolean;
  payload?: any;
  timestamp: string;
}

interface SlashCommand {
  command: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

/** Re-export the service's VoiceStreamEvent so the prop type stays stable. */
type VoiceStreamEvent = ServiceVoiceStreamEvent;

interface ChatViewProps {
  messages: Message[];
  loading: boolean;
  onSendMessage: (message: string, timestamp: string) => void;
  isMuted?: boolean;
  onScenarioClick?: (scenario: { question: string; answer: string }) => void;
  listening: boolean;
  isMaximized: boolean;
  /** Legacy prop: start browser Web Speech API listening */
  startListening: () => void;
  /** Legacy prop: stop browser Web Speech API listening */
  stopListening: () => void;
  /** Legacy prop: abort browser Web Speech API listening */
  abortListening: () => void;
  onRestart?: () => void;
  onToggleMute?: () => void;
  onShowShortcuts?: () => void;
  /**
   * Called after a successful voice round-trip.
   * transcription: the text the user spoke
   * payload: the last SSE event containing response + type fields
   */
  onVoiceResponse?: (transcription: string, payload: VoiceStreamEvent) => void;
  /** Tenant-configured conversational UI base URL (e.g. https://dev-riderapp.ecolane.com/conv-ui). */
  convUiBaseUrl?: string;
}

interface WelcomeViewProps {
  onScenarioClick: (question: string) => void;
}

const WelcomeView: React.FC<WelcomeViewProps> = ({ onScenarioClick }) => {
  const { t } = useTranslation();
  const { scenarios, isLoading, error } = useScenarios();
  if (isLoading) {
    return <div className="p-6">{t('alpha.chat.loadingScenarios')}</div>;
  }

  if (error) {
    return <div className="p-6 text-error">{error}</div>;
  }

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'ClipboardList':
        return <ClipboardList size={20} />;
      case 'Search':
        return <Search size={20} />;
      default:
        return null;
    }
  };
  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold">{t('alpha.chat.welcome')}</h1>
      <p className="mt-1 text-sm text-text-muted">
        {t('alpha.chat.chooseScenario')}
      </p>
      <div className="mt-6 space-y-4 @[700px]:grid @[700px]:grid-cols-2 @[700px]:gap-4">
        {scenarios.map((scenario: Scenario) => (
          <button
            key={scenario.title}
            onClick={() => onScenarioClick(scenario.title)}
            className={`scenario-button group transition-colors duration-200 ${scenario.hoverBorder} ${scenario.hoverBg}`}
          >
            <div className="flex items-center">
              <div
                className={`mr-4 flex h-10 w-10 items-center justify-center rounded-lg transition-colors duration-200 ${scenario.color} ${scenario.hoverColor}`}
              >
                <span
                  className={`transition-colors duration-200 ${scenario.textColor}`}
                >
                  {getIcon(scenario.icon)}
                </span>
              </div>
              <div>
                <p className="font-semibold">{scenario.title}</p>
                <p className="text-sm text-text-muted">
                  {scenario.description}
                </p>
              </div>
            </div>
            <ChevronRight
              className={`text-text-muted transition-colors duration-200 ${scenario.hoverTextColor}`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const ChatView: React.FC<ChatViewProps> = ({
  messages,
  loading,
  onSendMessage,
  isMuted,
  listening,
  startListening,
  stopListening,
  abortListening,
  onRestart,
  onToggleMute,
  onShowShortcuts,
  onVoiceResponse,
  convUiBaseUrl,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  // Internal recording state driven by MediaRecorder
  const [isRecording, setIsRecording] = useState(false);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const pcmSamplesRef = useRef<Int16Array[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const slashCommands: SlashCommand[] = [
    {
      command: '/restart',
      label: '/restart',
      description: t('alpha.chat.slashRestart'),
      icon: <RefreshCw size={20} className="text-primary" />,
    },
    {
      command: '/mute',
      label: '/mute',
      description: t('alpha.chat.slashMute'),
      icon: isMuted ? (
        <VolumeX size={20} className="text-text-muted" />
      ) : (
        <Volume2 size={20} className="text-text-muted" />
      ),
    },
    {
      command: '/shortcuts',
      label: '/shortcuts',
      description: t('alpha.chat.slashShortcuts'),
      icon: <Keyboard size={20} className="text-secondary" />,
    },
  ];

  // Derive the "actively listening" state: either MediaRecorder is running or
  // the legacy Web Speech API prop says so.
  const activelyListening = isRecording || listening;

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, loading, isVoiceProcessing]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (activelyListening) {
      interval = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [activelyListening]);

  /**
   * Delegates to sendVoice() in alpha.service.ts which calls the environment-resolved
   * voice endpoint (Dev: conv-ui/voice, QA/Demo: conv-ui-qa/voice) with streaming SSE.
   * On success: surfaces transcription as a user message and calls onVoiceResponse.
   * Also plays audio response if audio chunks are received.
   */
  const sendVoiceToApi = useCallback(
    async (audioBlob: Blob): Promise<void> => {
      setIsVoiceProcessing(true);
      setVoiceError(null);

      try {
        const { transcription, payload, audioChunks } = await sendVoice(audioBlob, convUiBaseUrl);

        if (transcription) {
          const ts = new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          });
          onSendMessage(transcription, ts);
        }

        if (onVoiceResponse && transcription) {
          onVoiceResponse(transcription, payload);
        }

        // Play audio response if available and not muted
        if (audioChunks.length > 0 && !isMuted) {
          logger.debug('Audio chunks received:', audioChunks.length);
          logger.debug('First chunk sample (first 100 chars):', audioChunks[0]?.substring(0, 100));
          
          setIsPlayingAudio(true);

          // Track whether audio completed successfully so we don't
          // show an error if the browser fires a spurious 'error' event
          // after the 'ended' event (e.g. when the object URL is revoked).
          let audioEnded = false;

          playBase64AudioChunks(audioChunks, {
            autoPlay: true,
            volume: 1.0,
            onEnded: () => {
              audioEnded = true;
              setIsPlayingAudio(false);
            },
            onError: (error) => {
              logger.error('Audio playback error:', error);
              setIsPlayingAudio(false);
              setVoiceError('Failed to play audio response.');
            },
          }).catch((audioErr) => {
            // Only surface the error if the audio never completed — prevents
            // the .catch() from firing on browsers that reject play() after
            // the audio already finished successfully.
            if (!audioEnded) {
              logger.error('Audio playback failed:', audioErr);
              setIsPlayingAudio(false);
              setVoiceError('Failed to play audio response.');
            }
          });
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Voice API request failed.';
        setVoiceError(errorMessage);
      } finally {
        setIsVoiceProcessing(false);
      }
    },
    [onSendMessage, onVoiceResponse, isMuted],
  );

  /**
   * Merges Int16Array PCM chunks into a single audio/pcm Blob.
   */
  const buildPcmBlob = (chunks: Int16Array[]): Blob => {
    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    const merged = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    return new Blob([merged.buffer], { type: 'audio/pcm' });
  };

  /**
   * Captures raw PCM16 mono 16 kHz using the Web Audio API.
   *
   * MediaRecorder only produces compressed audio (webm/opus, ogg/opus) which
   * the voice API cannot correctly transcribe. We use AudioContext +
   * ScriptProcessorNode to intercept float32 samples, downsample to 16 kHz,
   * and convert them to signed Int16 — the format required by voice-guide.md.
   *
   * Falls back to the legacy startListening() prop when getUserMedia is
   * unavailable (e.g. non-secure context).
   */
  const startMediaRecorder = useCallback(async (): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      startListening();
      return;
    }

    setVoiceError(null);
    pcmSamplesRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      // Request 16 kHz context; browser may honour it or resample internally
      const ctx = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      // ScriptProcessorNode is universally supported without extra bundling
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        const float32 = e.inputBuffer.getChannelData(0);
        // Convert float32 [-1, 1] → Int16 PCM16
        const int16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          const clamped = Math.max(-1, Math.min(1, float32[i]));
          int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
        }
        pcmSamplesRef.current.push(int16);
      };

      source.connect(processor);
      processor.connect(ctx.destination);

      setIsRecording(true);
    } catch (err: unknown) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Microphone access denied or unavailable.';
      setVoiceError(errorMessage);
    }
  }, [sendVoiceToApi, startListening]);

  /**
   * Stops PCM capture, tears down the AudioContext, and sends the
   * accumulated PCM16 blob to the voice API.
   */
  const stopMediaRecorder = useCallback((): void => {
    if (!audioContextRef.current) {
      stopListening();
      return;
    }

    processorRef.current?.disconnect();
    processorRef.current = null;

    const chunks = [...pcmSamplesRef.current];
    pcmSamplesRef.current = [];

    audioContextRef.current.close().finally(() => {
      audioContextRef.current = null;
    });

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    setIsRecording(false);

    const pcmBlob = buildPcmBlob(chunks);
    if (pcmBlob.size > 0) {
      sendVoiceToApi(pcmBlob);
    }
  }, [sendVoiceToApi, stopListening]);

  /**
   * Aborts capture and discards all audio without calling the API.
   */
  const abortMediaRecorder = useCallback((): void => {
    if (!audioContextRef.current) {
      abortListening();
      return;
    }

    processorRef.current?.disconnect();
    processorRef.current = null;
    pcmSamplesRef.current = [];

    audioContextRef.current.close().finally(() => {
      audioContextRef.current = null;
    });

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    setIsRecording(false);
  }, [abortListening]);

  const handleListen = (): void => {
    if (activelyListening) {
      stopMediaRecorder();
    } else {
      startMediaRecorder();
    }
  };

  const handleSend = (): void => {
    if (activelyListening) {
      stopMediaRecorder();
    } else if (message.trim()) {
      // Clear any stale voice error so it doesn't persist across text searches
      setVoiceError(null);
      const timestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      onSendMessage(message, timestamp);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Handle slash menu navigation
    if (showSlashMenu) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex((prev) =>
          prev < slashCommands.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex((prev) =>
          prev > 0 ? prev - 1 : slashCommands.length - 1
        );
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleCommandSelect(slashCommands[selectedCommandIndex].command);
      } else if (e.key === 'Escape') {
        setShowSlashMenu(false);
        setSelectedCommandIndex(0);
      }
      return;
    }

    // Send message on Enter key (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMessageChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    const value = e.target.value;
    setMessage(value);

    // Show slash menu when user types "/"
    if (value === '/') {
      setShowSlashMenu(true);
      setSelectedCommandIndex(0);
    } else if (value.startsWith('/') && value.length > 1) {
      // Keep menu open while typing command
      setShowSlashMenu(true);
    } else {
      setShowSlashMenu(false);
      setSelectedCommandIndex(0);
    }
  };

  const handleCommandSelect = (command: string): void => {
    setShowSlashMenu(false);
    setSelectedCommandIndex(0);
    setMessage('');

    // Execute the command action
    switch (command) {
      case '/restart':
        if (onRestart) {
          onRestart();
        }
        break;
      case '/mute':
        if (onToggleMute) {
          onToggleMute();
        }
        break;
      case '/shortcuts':
        if (onShowShortcuts) {
          onShowShortcuts();
        }
        break;
      default:
        break;
    }

    textareaRef.current?.focus();
  };

  const handleDelete = (): void => {
    if (activelyListening) {
      abortMediaRecorder();
    }
    setMessage('');
    setVoiceError(null);
  };

  const readAloud = (text: string): void => {
    if (!isMuted) {
      const synth = window.speechSynthesis;
      const utterance = new SpeechSynthesisUtterance(text);
      synth.speak(utterance);
    }
  };

  // Suppress unused-variable lint warning for readAloud (kept for future use)
  void readAloud;

  return (
    <div className="flex h-full flex-col @container">
      <div
        ref={chatContainerRef}
        className="flex-grow overflow-y-auto"
      >
        {messages.length > 0 ? (
          <div className="space-y-4 p-4">
            {messages.map((msg, index) => (
              <div key={index}>
                {msg.isUser ? (
                  <div className="flex flex-col items-end">
                    <div className="group relative rounded-lg bg-primary p-3 text-on-primary user-message">
                      <p>{msg.text}</p>
                    </div>
                    <span className="text-xs text-text-muted mt-1">
                      {msg.timestamp}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-start">
                    <div className="group relative rounded-lg border bg-primary/10 p-2 text-text ai-message ">
                      {(() => {
                        // Check if payload has type at root level (new format)
                        if (msg.payload?.type) {
                          const { type, response } = msg.payload;

                          // Check if response has kpis array - handle all KPI-based responses generically
                          if (response?.kpis && Array.isArray(response.kpis)) {
                            return (
                              <KpiCard
                                title={response.title}
                                kpis={response.kpis.map((kpi: any) => ({
                                  title: kpi.kpi,
                                  value: kpi.value,
                                }))}
                              />
                            );
                          }

                          // Handle other response types based on type field
                          switch (type) {
                            default:
                              // Fallback to response rendering
                              if (response) {
                                return <p>{JSON.stringify(response, null, 2)}</p>;
                              }
                              return msg.text ? <p>{msg.text}</p> : null;
                          }
                        }

                        // Legacy format: response.type
                        const response = msg.payload?.response || msg.payload;

                        if (!response) {
                          return msg.text ? <p>{msg.text}</p> : null;
                        }

                        switch (response.type) {
                          case 'kpi':
                            return (
                              <KpiCard
                                title={response.title}
                                kpis={
                                  Array.isArray(response.value)
                                    ? response.value
                                    : [
                                        {
                                          title: response.title,
                                          value: response.value,
                                        },
                                      ]
                                }
                              />
                            );
                          case 'topn':
                            return (
                              <div className="w-full">
                                <KpiCard
                                  title={response.title}
                                  kpis={Array.isArray(response.rows)
                                    ? response.rows.map((row: (string | number)[]) => ({
                                        title: String(row[0] ?? 'N/A'),
                                        value: row[1],
                                      }))
                                    : []}
                                />
                                {Array.isArray(response.ignored_filters) && response.ignored_filters.length > 0 && (
                                  <p className="mt-2 text-xs text-text-muted">
                                    Ignored filters: {response.ignored_filters.join(', ')}
                                  </p>
                                )}
                              </div>
                            );
                          case 'table':
                            return <pre>{JSON.stringify(response.rows, null, 2)}</pre>;
                          case 'text':
                            return <p>{response.raw || response.response}</p>;
                          case 'bar':
                            return (
                              <KpiCard
                                title={response.title}
                                kpis={response.rows.map((row: any) => ({
                                  title: row[0] || 'N/A',
                                  value: row[1],
                                }))}
                              />
                            );
                          default:
                            return msg.text ? <p>{msg.text}</p> : null;
                        }
                      })()}
                    </div>
                    <span className="text-xs text-text-muted mt-1">
                      {msg.timestamp}
                    </span>
                  </div>
                )}
              </div>
            ))}
            {(loading || isVoiceProcessing || isPlayingAudio) && (
              <div className="flex flex-col items-start">
                <div className="group relative rounded-lg border bg-primary/10 p-3 text-text ai-message">
                  <div className="flex items-center justify-center">
                    {isPlayingAudio ? (
                      <div className="flex items-center space-x-2">
                        <Volume2 size={16} className="text-primary animate-pulse" />
                        <span className="text-sm text-text-muted">Playing audio response...</span>
                      </div>
                    ) : (
                      <>
                        <div
                          className="typing-indicator-dot"
                          style={{ animationDelay: '0s' }}
                        ></div>
                        <div
                          className="typing-indicator-dot"
                          style={{ animationDelay: '0.2s' }}
                        ></div>
                        <div
                          className="typing-indicator-dot"
                          style={{ animationDelay: '0.4s' }}
                        ></div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <WelcomeView
            onScenarioClick={(q) => {
              const timestamp = new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              });
              onSendMessage(q, timestamp);
            }}
          />
        )}
      </div>

      {/* Voice error banner */}
      {voiceError && (
        <div
          role="alert"
          className="mx-4 mb-2 rounded-lg border border-error bg-error/10 px-4 py-2 text-sm text-error"
        >
          {voiceError}
        </div>
      )}

      <div className="border-t border-border p-4">
        {activelyListening ? (
          <div className="flex h-24 w-full items-center justify-between rounded-2xl border border-border-strong bg-surface px-4 py-3">
            <button
              onClick={handleDelete}
              className="text-error"
              aria-label={t('alpha.chat.cancelRecording')}
            >
              <Trash2 size={24} aria-hidden="true" />
            </button>
            <div className="flex flex-col items-center">
              <div className="flex h-8 items-center justify-center overflow-hidden">
                {Array.from({ length: 40 }).map((_, i) => (
                  <div
                    key={i}
                    className="mx-0.5 w-0.5 animate-pulse rounded-full bg-primary"
                    style={{
                      animationDuration: '1.5s',
                      animationDelay: `${i * 0.05}s`,
                      height: `${Math.floor(Math.random() * 80) + 20}%`,
                    }}
                  />
                ))}
              </div>
              <div className="mt-2 flex items-center space-x-2 rounded-full bg-error px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-on-primary" />
                <span className="text-sm font-semibold text-on-primary">
                  {new Date(recordingTime * 1000)
                    .toISOString()
                    .substr(14, 5)}
                </span>
              </div>
            </div>
            <button
              onClick={handleSend}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-on-primary"
              aria-label={t('alpha.chat.stopAndSend')}
            >
              <Send size={24} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className="relative w-full">
            {/* Slash Command Menu */}
            {showSlashMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-border bg-surface shadow-lg">
                <div className="p-2">
                  {slashCommands
                    .filter((cmd) =>
                      cmd.command.toLowerCase().includes(message.toLowerCase())
                    )
                    .map((cmd, index) => (
                      <button
                        key={cmd.command}
                        onClick={() => handleCommandSelect(cmd.command)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                          index === selectedCommandIndex
                            ? 'bg-primary/10'
                            : 'hover:bg-primary/5'
                        }`}
                        onMouseEnter={() => setSelectedCommandIndex(index)}
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-alt">
                          {cmd.icon}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-text">
                            {cmd.label}
                          </p>
                          <p className="text-sm text-text-muted">
                            {cmd.description}
                          </p>
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              placeholder={t('alpha.chat.placeholder')}
              className="h-24 w-full resize-none rounded-2xl border border-border-strong bg-surface py-3 pl-12 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              rows={3}
            />
            <button
              onClick={handleListen}
              className={`absolute left-4 bottom-4 ${
                isVoiceProcessing ? 'text-primary animate-pulse' : 'text-text-muted'
              }`}
              aria-label={t('alpha.chat.startVoice')}
              disabled={isVoiceProcessing}
            >
              <Mic size={20} aria-hidden="true" />
            </button>
            <button
              onClick={handleSend}
              className="absolute right-4 bottom-4"
              aria-label={t('alpha.chat.send')}
            >
              <Send size={20} className="text-text-muted" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatView;